// Visual accessibility (decision 0063: text size and contrast): a high-contrast theme derived from the game's own,
// the WCAG contrast ratio to check a theme with, and the UI's custom properties as a record, so a game can put the
// same variables on the document's root and every overlay outside the screens (the HUD, subtitles, captions) follows
// the player's text size and contrast too.
import type { UiTheme } from "./style";

/** The high-contrast theme: solid black panels, white text, a saturated accent and focus ring (7:1 or better). */
export function highContrastTheme(base: UiTheme): UiTheme {
  return {
    ...base,
    text: "#ffffff",
    muted: "#e8e8e8",
    bg: "#000000",
    panel: "rgba(0, 0, 0, 0.98)",
    scrim: "rgba(0, 0, 0, 0.82)",
    splash: "linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.9))",
    button: "#000000",
    buttonHover: "#262626",
    accent: "#ffd400",
    accentText: "#000000",
    focus: "#00e5ff",
    warn: "#ffb0a0",
  };
}

/** Parse `#rgb`, `#rrggbb` or `rgb(a)(r, g, b, a)` into 0..255 channels (alpha ignored). */
export function parseColour(c: string): [number, number, number] | null {
  const s = c.trim();
  const hex = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(s);
  if (hex) {
    const h = hex[1]!.length === 3 ? [...hex[1]!].map((x) => x + x).join("") : hex[1]!;
    return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
  }
  const rgb = /^rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)/i.exec(s);
  return rgb ? [Number(rgb[1]), Number(rgb[2]), Number(rgb[3])] : null;
}

/** WCAG relative luminance of a colour. */
export function luminance(c: string): number {
  const p = parseColour(c);
  if (!p) return NaN;
  const lin = p.map((v) => {
    const x = v / 255;
    return x <= 0.03928 ? x / 12.92 : Math.pow((x + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * lin[0]! + 0.7152 * lin[1]! + 0.0722 * lin[2]!;
}

/** The WCAG contrast ratio between two colours (1 to 21). */
export function contrastRatio(a: string, b: string): number {
  const la = luminance(a);
  const lb = luminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}
